import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Truck } from 'lucide-react';
import { ordersAPI, paymentAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';
import { useSettings } from '../contexts/SettingsContext';
import { renderGoogleCustomerReviewsOptIn } from '../utils/googleCustomerReviews';

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCleared, setCartCleared] = useState(false);
  const { settings } = useSettings();
  const paymentToastShown = useRef(false);
  const orderCreatedToastShown = useRef(false);
  const fetchStarted = useRef(false);

  useEffect(() => {
    if (!order) return;
    const deliveryDays = settings?.shipping?.estimatedDeliveryDays ?? 3;
    renderGoogleCustomerReviewsOptIn(order, { estimatedBusinessDays: deliveryDays + 2 });
  }, [order, settings?.shipping?.estimatedDeliveryDays]);

  useEffect(() => {
    if (!orderId || fetchStarted.current) {
      return;
    }
    fetchStarted.current = true;

    const fetchOrder = async () => {
      try {
        let response;
        try {
          response = await ordersAPI.getOrder(orderId);
        } catch (authError) {
          response = await ordersAPI.getPublicOrder(orderId);
        }
        const orderData = response.data.order;
        setOrder(orderData);

        if (!cartCleared) {
          try {
            await clearCart();
            setCartCleared(true);
          } catch (cartError) {
            console.error('Sepet temizlenemedi:', cartError);
          }
        }

        const status = searchParams.get('status');
        const paymentStatusParam = searchParams.get('payment_status');
        const paymentId = searchParams.get('payment_id');
        const fromPaymentReturn =
          status === 'success' || paymentStatusParam || paymentId;

        if (fromPaymentReturn) {
          try {
            const statusResponse = await paymentAPI.getPaymentStatus(orderId);
            const { paymentStatus: currentStatus } = statusResponse.data;

            if (currentStatus === 'odendi') {
              if (!paymentToastShown.current) {
                paymentToastShown.current = true;
                toast.success('✅ Ödeme başarıyla tamamlandı!', {
                  id: 'order-payment-success',
                  duration: 3000
                });
              }

              let updatedResponse;
              try {
                updatedResponse = await ordersAPI.getOrder(orderId);
              } catch (authError) {
                updatedResponse = await ordersAPI.getPublicOrder(orderId);
              }
              setOrder(updatedResponse.data.order);

              navigate(`/order-success/${orderId}`, { replace: true });
            } else if (currentStatus === 'iptal' && !paymentToastShown.current) {
              paymentToastShown.current = true;
              toast.error('Ödeme işlemi başarısız oldu.', { id: 'order-payment-failed' });
            }
          } catch (error) {
            console.error('Ödeme durumu kontrol edilemedi:', error);
          }
        } else if (!orderCreatedToastShown.current) {
          orderCreatedToastShown.current = true;
          toast.success('✅ Siparişiniz başarıyla oluşturuldu!', {
            id: 'order-created-success',
            duration: 3000
          });
        }
      } catch (error) {
        console.error('Sipariş bilgisi alınamadı:', error);
        toast.error('Sipariş bilgisi yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca orderId ile tek sefer yükle
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Sipariş bilgileri yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sipariş Bulunamadı</h2>
            <p className="text-gray-600 mb-6">Aradığınız sipariş bulunamadı.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Başarı Mesajı */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg shadow-lg p-8 text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-green-500 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            🎉 Siparişiniz Başarıyla Oluşturuldu!
          </h1>
          <p className="text-lg text-gray-700 mb-2">
            Sipariş numaranız: <span className="font-bold text-orange-600 text-xl">#{order.order_number || order.id}</span>
          </p>
          {order.payment_status === 'odendi' && (
            <div className="mt-4 inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
              ✓ Ödeme Başarılı
            </div>
          )}
          {order.payment_status === 'odendi' && (
            <p className="mt-4 text-sm text-gray-600 max-w-lg mx-auto">
              Siparişiniz hakkında Google üzerinden kısa bir değerlendirme daveti alabilirsiniz.
            </p>
          )}
          <div id="google-customer-reviews-optin" className="mt-2 min-h-[1px]" aria-hidden="true" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sol Taraf - Sipariş Bilgileri */}
          <div className="space-y-6">
            {/* Sipariş Özeti */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sipariş Özeti</h2>
              
              <div className="space-y-4">
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => {
                    // Görsel yolu kontrolü - object veya string olabilir
                    let productImage = item.product?.images?.[0] || item.image || '/images/placeholder.jpg';
                    if (typeof productImage === 'object' && productImage?.url) {
                      productImage = productImage.url;
                    } else if (typeof productImage !== 'string') {
                      productImage = '/images/placeholder.jpg';
                    }
                    
                    const productName = item.product?.name || item.name || 'Ürün';
                    const itemPrice = item.price || 0;
                    const itemQuantity = item.quantity || 1;
                    
                    return (
                      <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <img
                          src={productImage && typeof productImage === 'string' && productImage.startsWith('http') 
                            ? productImage 
                            : productImage && typeof productImage === 'string' && productImage.startsWith('/') 
                              ? productImage 
                              : `/uploads/${productImage}`}
                          alt={productName}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.src = '/images/placeholder.jpg';
                          }}
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{productName}</h3>
                          <p className="text-sm text-gray-600">Adet: {itemQuantity}</p>
                          {item.product?.sku && (
                            <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 text-lg">
                            ₺{(itemPrice * itemQuantity).toFixed(2)}
                          </p>
                          {itemQuantity > 1 && (
                            <p className="text-xs text-gray-500">₺{itemPrice.toFixed(2)} / adet</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-4">Sipariş detayları yükleniyor...</p>
                )}
              </div>

              <div className="border-t-2 border-gray-200 pt-4 mt-4 space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Ara Toplam:</span>
                  <span className="font-semibold">₺{parseFloat(order.subtotal || order.subtotal_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Kargo Ücreti:</span>
                  <span className="font-semibold">
                    {parseFloat(order.shippingCost || order.shipping_cost || 0) > 0 
                      ? `₺${parseFloat(order.shippingCost || order.shipping_cost || 0).toFixed(2)}` 
                      : 'Ücretsiz'}
                  </span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t-2 border-gray-300 pt-3 text-gray-900">
                  <span>Toplam Tutar:</span>
                  <span className="text-orange-600">₺{parseFloat(order.totalAmount || order.total || order.total_amount || 0).toFixed(2)}</span>
                </div>
                {order.payment_status === 'odendi' && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-center mt-3">
                    <p className="text-green-800 font-semibold text-sm">
                      ✓ Bu sipariş için ödeme alınmıştır
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Teslimat Bilgileri */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <Truck className="w-5 h-5 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Teslimat Bilgileri</h2>
              </div>
              
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-700">Ad Soyad:</span>
                  <p className="text-gray-900 mt-1">
                    {order.shippingAddress?.firstName || order.shipping_name?.split(' ')[0]} {order.shippingAddress?.lastName || order.shipping_name?.split(' ').slice(1).join(' ') || ''}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Adres:</span>
                  <p className="text-gray-900 mt-1">{order.shippingAddress?.address || order.shipping_address}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-semibold text-gray-700">Şehir:</span>
                    <p className="text-gray-900 mt-1">{order.shippingAddress?.city || order.shipping_city}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">İlçe:</span>
                    <p className="text-gray-900 mt-1">{order.shippingAddress?.state || order.shipping_state || '-'}</p>
                  </div>
                </div>
                {order.shippingAddress?.zipCode && (
                  <div>
                    <span className="font-semibold text-gray-700">Posta Kodu:</span>
                    <p className="text-gray-900 mt-1">{order.shippingAddress.zipCode}</p>
                  </div>
                )}
                <div>
                  <span className="font-semibold text-gray-700">Telefon:</span>
                  <p className="text-gray-900 mt-1">{order.contactInfo?.phone || order.shipping_phone || order.contact_phone}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">E-posta:</span>
                  <p className="text-gray-900 mt-1">{order.contactInfo?.email || order.guest_email || order.contact_email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sağ Taraf - Durum ve Sonraki Adımlar */}
          <div className="space-y-6">
            {/* Sipariş Durumu */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sipariş Durumu</h2>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Sipariş Onaylandı</p>
                    <p className="text-sm text-gray-600">Siparişiniz alındı ve işleme alındı</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                    <Package className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Hazırlanıyor</p>
                    <p className="text-sm text-gray-600">Siparişiniz hazırlanıyor</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                    <Truck className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Kargoya Verildi</p>
                    <p className="text-sm text-gray-600">Siparişiniz kargoya verildi</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                    <CheckCircle className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Teslim Edildi</p>
                    <p className="text-sm text-gray-600">Siparişiniz teslim edildi</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ödeme Bilgileri */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Ödeme Bilgileri</h2>
              
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-700">Ödeme Yöntemi:</span>
                  <p className="text-gray-900 mt-1">
                    {order.paymentMethod === 'creditCard' || order.payment_method === 'creditCard' || order.payment_method === 'kredi_karti' ? '💳 Kredi Kartı' : ''}
                    {order.paymentMethod === 'bankTransfer' || order.payment_method === 'bankTransfer' || order.payment_method === 'banka_havalesi' ? '🏦 Banka Havalesi' : ''}
                    {!order.paymentMethod && !order.payment_method && '-'}
                  </p>
                  {order.payment_status === 'odendi' && (
                    <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Ödendi</span>
                  )}
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Teslimat Yöntemi:</span>
                  <p className="text-gray-900 mt-1">
                    {order.deliveryMethod === 'standard' || order.delivery_method === 'standard' ? '🚚 Standart Teslimat (3-5 iş günü)' : ''}
                    {order.deliveryMethod === 'express' || order.delivery_method === 'express' ? '⚡ Hızlı Teslimat (1-2 iş günü)' : ''}
                    {!order.deliveryMethod && !order.delivery_method && 'Standart Teslimat'}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Sipariş Tarihi:</span>
                  <p className="text-gray-900 mt-1">
                    {new Date(order.createdAt || order.created_at).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {order.payment_id && (
                  <div>
                    <span className="font-semibold text-gray-700">Ödeme ID:</span>
                    <p className="text-gray-900 mt-1 text-sm font-mono">{order.payment_id}</p>
                  </div>
                )}
                {order.notes && (
                  <div>
                    <span className="font-semibold text-gray-700">Sipariş Notları:</span>
                    <p className="text-gray-900 mt-1 italic">{order.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Aksiyon Butonları */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sonraki Adımlar</h2>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/orders')}
                  className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Siparişlerimi Görüntüle
                </button>
                
                <button
                  onClick={() => navigate('/products')}
                  className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Alışverişe Devam Et
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Ana Sayfaya Dön
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
